import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AvatarBadge, AVATAR_ICONS } from "@/components/AvatarBadge";
import { CHARACTERS as TGCF } from "@/components/tgcfData";
import { DR } from "@/components/danganronpaData";
import { AD } from "@/components/apothecaryData";
import { WH } from "@/components/witchHatData";
import { IV } from "@/components/villainessData";
import { MD } from "@/components/mdzsData";

const FANDOMS = [
  ["TGCF", TGCF], ["Danganronpa", DR.CHARACTERS], ["Apothecary Diaries", AD.CHARACTERS],
  ["Witch Hat Atelier", WH.CHARACTERS], ["Inept Villainess", IV.CHARACTERS], ["Master of Diabolism", MD.CHARACTERS],
];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const colorsOf = (c) => (c.colors ?? [...new Set(c.beads)]).slice(0, 3).concat(["#f2f0ea", "#c8a2ff"]).slice(0, 3);
const PARTS = ["Background", "Moon", "Ring"];

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${active ? "bg-amber-200 text-[#0e0616] border-amber-200" : "border-purple-200/30 text-purple-50 hover:border-purple-100"}`}>
    {children}
  </button>
);

export const AvatarBuilder = () => {
  const { user, setUser, avatarOpen, setAvatarOpen } = useAuth();
  const [palette, setPalette] = useState([]);
  const [fandom, setFandom] = useState(FANDOMS[0][0]);
  const [av, setAv] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!avatarOpen || palette.length) return;
    http.get("/designs/palette").then((r) => setPalette(Array.isArray(r.data?.colors) ? r.data.colors : [])).catch(() => {});
  }, [avatarOpen, palette.length]);

  useEffect(() => {
    if (!avatarOpen || !user) return;
    setAv(user.avatar ?? { character: "", fandom: "", colors: ["#2b2d6e", "#f2f0ea", "#c8a2ff"], icon: "moon", label: user.name?.split(" ")[0]?.slice(0, 16) ?? "" });
    if (user.avatar?.fandom) setFandom(user.avatar.fandom);
  }, [avatarOpen, user]);

  const chars = useMemo(() => FANDOMS.find(([f]) => f === fandom)?.[1] ?? [], [fandom]);
  if (!avatarOpen || !user || !av) return null;

  const pick = (c) => setAv((a) => ({ ...a, character: c.name, fandom, colors: colorsOf(c) }));
  const setColor = (i, hex) => setAv((a) => ({ ...a, colors: a.colors.map((x, j) => (j === i ? hex : x)) }));
  const close = async (skip) => {
    if (skip && !user.avatar_prompted) {
      try { const { data } = await http.post("/me/avatar/skip"); setUser(data); } catch { /* ignore */ }
    }
    setAvatarOpen(false);
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.put("/me/avatar", av);
      setUser(data);
      setAvatarOpen(false);
      toast.success("Your avatar is ready!");
    } catch {
      toast.error("Couldn't save your avatar. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="avatar-builder">
      <div className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-[28px] sm:rounded-[28px] border border-purple-200/25 bg-[#170a24] p-5 sm:p-8" data-lenis-prevent>
        <button onClick={() => close(true)} aria-label="Close" data-testid="avatar-close-btn" className="absolute right-4 top-4 p-2 text-purple-200 hover:text-white"><X className="w-5 h-5" /></button>
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-200">Your Lavender Hollow avatar</p>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl">Make your moon badge</h2>
        <div className="mt-6 grid sm:grid-cols-[180px_1fr] gap-6">
          <div className="flex flex-col items-center gap-2">
            <AvatarBadge avatar={av} size={150} testid="avatar-preview" />
            <p className="font-display text-xl" data-testid="avatar-preview-label">{av.label || " "}</p>
            {av.character && <p className="text-xs text-purple-100/85 text-center">Inspired by {av.character}</p>}
          </div>
          <div className="space-y-5">
            <div>
              <p className="text-sm text-purple-100 mb-2">1. Pick your favorite character</p>
              <div className="flex flex-wrap gap-2">
                {FANDOMS.map(([f]) => <Chip key={f} active={fandom === f} onClick={() => setFandom(f)} testid={`avatar-fandom-${slug(f)}`}>{f}</Chip>)}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                {chars.map((c) => <Chip key={c.name} active={av.character === c.name} onClick={() => pick(c)} testid={`avatar-char-${slug(c.name)}`}>{c.name}</Chip>)}
              </div>
            </div>
            <div>
              <p className="text-sm text-purple-100 mb-2">2. Change the colors</p>
              {PARTS.map((part, i) => (
                <div key={part} className="flex items-center gap-2 mb-2">
                  <span className="w-24 text-xs text-purple-100/85">{part}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {palette.map((p) => (
                      <button key={p.hex} onClick={() => setColor(i, p.hex)} title={p.name} aria-label={`${part} ${p.name}`}
                        data-testid={`avatar-color-${i}-${slug(p.name)}`}
                        className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${av.colors[i] === p.hex ? "border-amber-200 scale-110" : "border-transparent"}`}
                        style={{ background: p.hex }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm text-purple-100 mb-2">3. Add an icon</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(AVATAR_ICONS).map(([k, Icon]) => (
                  <button key={k} onClick={() => setAv((a) => ({ ...a, icon: k }))} aria-label={k} data-testid={`avatar-icon-${k}`}
                    className={`w-10 h-10 rounded-xl grid place-items-center border transition-colors ${av.icon === k ? "bg-amber-200 text-[#0e0616] border-amber-200" : "border-purple-200/30 hover:border-purple-100"}`}>
                    <Icon className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm text-purple-100 mb-2">4. Add your name</p>
              <input value={av.label} maxLength={16} onChange={(e) => setAv((a) => ({ ...a, label: e.target.value }))}
                data-testid="avatar-label-input" placeholder="Your name"
                className="w-full rounded-xl border border-purple-200/30 bg-black/30 px-4 py-2.5 text-purple-50 focus:outline-none focus:border-amber-200/60" />
            </div>
          </div>
        </div>
        <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button onClick={() => close(true)} data-testid="avatar-skip-btn" className="rounded-full border border-purple-200/30 px-6 py-3 text-sm hover:border-purple-100">
            {user.avatar_prompted ? "Cancel" : "Skip for now"}
          </button>
          <button onClick={save} disabled={busy || !av.character} data-testid="avatar-save-btn"
            className="rounded-full bg-purple-100 text-[#0e0616] px-6 py-3 text-sm font-medium hover:bg-amber-200 transition-colors disabled:opacity-50">
            {busy ? "Saving…" : av.character ? "Save my avatar" : "Pick a character first"}
          </button>
        </div>
      </div>
    </div>
  );
};
